import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PasswordInput from '@/components/PasswordInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import LanguagePickerButton from '@/components/LanguagePickerButton';
import AppHeader from '@/components/AppHeader';
import Recaptcha, { RECAPTCHA_ENABLED } from '@/components/Recaptcha';
import { extractErrorMessage } from '@/lib/errors';
import { Loader2 } from 'lucide-react';

// Comprehensive list of country codes (sorted alphabetically by country)
const COUNTRY_CODES = [
  { code: '+93', country: 'Afghanistan' },
  { code: '+355', country: 'Albania' },
  { code: '+213', country: 'Algeria' },
  { code: '+244', country: 'Angola' },
  { code: '+54', country: 'Argentina' },
  { code: '+61', country: 'Australia' },
  { code: '+880', country: 'Bangladesh' },
  { code: '+229', country: 'Benin' },
  { code: '+267', country: 'Botswana' },
  { code: '+55', country: 'Brazil' },
  { code: '+226', country: 'Burkina Faso' },
  { code: '+257', country: 'Burundi' },
  { code: '+237', country: 'Cameroon' },
  { code: '+1', country: 'Canada/USA' },
  { code: '+236', country: 'Central African Rep.' },
  { code: '+235', country: 'Chad' },
  { code: '+86', country: 'China' },
  { code: '+57', country: 'Colombia' },
  { code: '+243', country: 'DR Congo' },
  { code: '+20', country: 'Egypt' },
  { code: '+251', country: 'Ethiopia' },
  { code: '+33', country: 'France' },
  { code: '+241', country: 'Gabon' },
  { code: '+220', country: 'Gambia' },
  { code: '+49', country: 'Germany' },
  { code: '+233', country: 'Ghana' },
  { code: '+224', country: 'Guinea' },
  { code: '+91', country: 'India' },
  { code: '+62', country: 'Indonesia' },
  { code: '+225', country: 'Ivory Coast' },
  { code: '+254', country: 'Kenya' },
  { code: '+231', country: 'Liberia' },
  { code: '+261', country: 'Madagascar' },
  { code: '+265', country: 'Malawi' },
  { code: '+60', country: 'Malaysia' },
  { code: '+223', country: 'Mali' },
  { code: '+222', country: 'Mauritania' },
  { code: '+52', country: 'Mexico' },
  { code: '+212', country: 'Morocco' },
  { code: '+258', country: 'Mozambique' },
  { code: '+264', country: 'Namibia' },
  { code: '+227', country: 'Niger' },
  { code: '+234', country: 'Nigeria' },
  { code: '+92', country: 'Pakistan' },
  { code: '+63', country: 'Philippines' },
  { code: '+48', country: 'Poland' },
  { code: '+242', country: 'Republic of Congo' },
  { code: '+250', country: 'Rwanda' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+221', country: 'Senegal' },
  { code: '+232', country: 'Sierra Leone' },
  { code: '+65', country: 'Singapore' },
  { code: '+252', country: 'Somalia' },
  { code: '+27', country: 'South Africa' },
  { code: '+211', country: 'South Sudan' },
  { code: '+34', country: 'Spain' },
  { code: '+94', country: 'Sri Lanka' },
  { code: '+249', country: 'Sudan' },
  { code: '+255', country: 'Tanzania' },
  { code: '+66', country: 'Thailand' },
  { code: '+228', country: 'Togo' },
  { code: '+216', country: 'Tunisia' },
  { code: '+90', country: 'Turkey' },
  { code: '+256', country: 'Uganda' },
  { code: '+971', country: 'UAE' },
  { code: '+44', country: 'UK' },
  { code: '+84', country: 'Vietnam' },
  { code: '+260', country: 'Zambia' },
  { code: '+263', country: 'Zimbabwe' },
].sort((a, b) => a.country.localeCompare(b.country));

export default function LoginPage() {
  const [countryCode, setCountryCode] = useState('+234');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim() || phoneNumber.length < 7) {
      toast({ title: t('auth.login.errors.invalidPhone'), variant: 'destructive' });
      return;
    }
    if (!password) {
      toast({ title: t('auth.login.errors.noPassword'), variant: 'destructive' });
      return;
    }
    if (RECAPTCHA_ENABLED && !captchaToken) {
      toast({ title: t('auth.login.errors.captcha'), variant: 'destructive' });
      return;
    }

    const fullPhone = `${countryCode}${phoneNumber.replace(/^0+/, '')}`;

    setLoading(true);
    try {
      await login(fullPhone, password, captchaToken);
      // Login succeeded — AuthContext has already saved tokens and set user.
      navigate('/dashboard');
    } catch (error) {
      // Deliberately generic — the backend gives the same 401 for both
      // "no such user" and "wrong password" to avoid enumeration.
      // v2 tokens are single-use, so reset the widget before the user
      // retries — otherwise the next submit always fails with
      // "Captcha check failed".
      recaptchaRef.current?.reset();
      toast({
        title: extractErrorMessage(error, t('auth.login.errors.failed')),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Shared branded header — logo centered, language chip on the right */}
      <AppHeader trailing={<LanguagePickerButton />} />

      <div className="flex-1 flex flex-col p-6">
        {/* Header */}
        <div className="text-center mt-6 mb-10">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.login.title')}</h1>
          <p className="text-gray-600">{t('auth.login.subtitle')}</p>
        </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1">
        <label htmlFor="login-phone" className="block text-sm font-semibold text-gray-900 mb-2">{t('auth.login.phoneLabel')}</label>
        <div className="flex gap-2 mb-6">
          <Select value={countryCode} onValueChange={setCountryCode}>
            <SelectTrigger className="w-[140px] h-14 bg-white border-gray-200" data-testid="country-code-select">
              <SelectValue>
                <span>{countryCode} {selectedCountry?.country?.slice(0, 3)}</span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {COUNTRY_CODES.map((country) => (
                <SelectItem key={country.code + country.country} value={country.code}>
                  {country.code} {country.country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            id="login-phone"
            type="tel"
            placeholder={t('auth.login.phonePlaceholder')}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
            className="flex-1 h-14 bg-white border-gray-200 text-lg"
            data-testid="login-phone-input"
          />
        </div>

        <label htmlFor="login-password" className="block text-sm font-semibold text-gray-900 mb-2">{t('auth.login.passwordLabel')}</label>
        <div className="mb-6">
          <PasswordInput
            id="login-password"
            placeholder={t('auth.login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-14 bg-white border-gray-200 text-lg"
            testId="login-password-input"
            autoComplete="current-password"
          />
        </div>

        <Recaptcha ref={recaptchaRef} onChange={setCaptchaToken} />

        <Button
          type="submit"
          disabled={loading || (RECAPTCHA_ENABLED && !captchaToken)}
          className="w-full h-14 bg-farm-primary hover:bg-farm-primary-dark text-white font-semibold text-lg rounded-xl mb-3"
          data-testid="login-submit-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('auth.login.submit')}
        </Button>

        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="w-full text-center text-sm font-medium text-farm-primary hover:underline mb-4 py-1"
          data-testid="forgot-password-link"
        >
          {t('auth.login.forgotPassword', { defaultValue: 'Forgot password?' })}
        </button>

        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/register')}
          className="w-full h-14 font-semibold text-lg rounded-xl border-2"
        >
          {t('auth.login.noAccount')}
        </Button>
      </form>
      </div>
    </div>
  );
}
